import {
    AfterViewInit,
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    EventEmitter,
    Input,
    OnDestroy,
    Optional,
    Output,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Column } from '../../entities/column.entity';
import { Comparable } from '../../entities/misc/comparable.interface';
import { Trackable } from '../../entities/misc/trackable.interface';
import { BaseColumnComponent } from '../../services/base-column-component';
import { ColumnHandlerService } from '../../services/column-handler.service';

@Component({
    standalone: false,
    selector: 'digihaven-cell',
    templateUrl: './cell.component.html',
    styleUrls: ['./cell.component.scss']
})
export class CellComponent<T extends Comparable & Trackable> implements OnDestroy, AfterViewInit {
    private readonly destroy$: Subject<void> = new Subject<void>();

    @ViewChild('dynamicInsert', { read: ViewContainerRef }) dynamicInsert!: ViewContainerRef;

    @Input() record!: T;
    @Input() column!: Column<T>;
    @Input() overviewId?: string;

    @Output()
    rowNavigation: EventEmitter<T> = new EventEmitter<T>();

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        @Optional() private handler?: ColumnHandlerService<T>
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.initDynamicComponent();
    }

    private initDynamicComponent(): void {
        const component: Type<BaseColumnComponent<T>> | undefined = this.handler?.getComponent(
            this.column.columnDefinition.toString()
        );
        const containerRef: ViewContainerRef = this.dynamicInsert;

        if (component && containerRef) {
            containerRef.clear();

            const componentFactory: ComponentFactory<BaseColumnComponent<T>> =
                this.componentFactoryResolver.resolveComponentFactory(component);

            const instance: BaseColumnComponent<T> = containerRef.createComponent(componentFactory).instance;
            instance.data = this.record;
            instance.fieldDefinition = this.column.fieldDefinition;
            instance.defaultValue = this.column.columnDefinition.defaultValue;
            instance.options = this.column.columnDefinition.options;
            instance.enabled = this.column.columnDefinition.enabledForRecord(this.record);
            instance.overviewId = this.overviewId;

            instance.rowNavigation.pipe(takeUntil(this.destroy$)).subscribe((data: T) => this.rowNavigation.next(data));
        }
    }
}
